import type {AvatarProps} from '@chakra-ui/react';
import {Avatar as Base, forwardRef} from '@chakra-ui/react';
import type {FC} from 'react';

import type {AvatarVariant} from './theme';
import {useAvatarStyleConfig} from './theme';
type Props = AvatarProps & {
  variant?: AvatarVariant;
  clickable?: boolean;
};

// Custom function to generate initials from a name
const getCustomInitials = (name: string) => {
  if (!name) return ``;

  // Remove common bracket patterns and their contents
  const nameWithoutBrackets = name
    .replace(/\s*\([^)]*\)\s*/g, ``) // Remove parentheses () and their contents
    .replace(/\s*\[[^\]]*\]\s*/g, ``) // Remove square brackets [] and their contents
    .replace(/\s*\{[^}]*\}\s*/g, ``) // Remove curly braces {} and their contents
    .replace(/\s*<[^>]*>\s*/g, ``); // Remove angle brackets <> and their contents

  // Remove any other punctuation and special characters
  const cleanName = nameWithoutBrackets.replace(/[^\w\s]|_/g, ``);

  // Split the name by spaces and filter out empty parts
  const nameParts = cleanName.split(` `).filter(part => part.length > 0);

  // Take first two parts and last part if there are more than 2 parts
  const selectedParts =
    nameParts.length > 2 ? [nameParts[0], nameParts[1], nameParts[nameParts.length - 1]] : nameParts;

  // Get the first letter of each selected part
  return selectedParts
    .map(part => part[0])
    .join(``)
    .toUpperCase();
};

export const Avatar: FC<Props> = forwardRef(({colorScheme, ...props}: Props, ref) => {
  const sx = useAvatarStyleConfig({...props, colorScheme});
  return <Base sx={{...sx.container}} getInitials={getCustomInitials} {...props} ref={ref} />;
});

export type {Props as AvatarProps};
